import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, UserPlus, ArrowRight, X } from 'lucide-react';

interface EmailChoiceDialogProps {
  email: string;
  onAnonymous: () => void;
  onMagicLink: () => void;
  onClose: () => void;
}

export function EmailChoiceDialog({ email, onAnonymous, onMagicLink, onClose }: EmailChoiceDialogProps) {
  const [isLoading, setIsLoading] = useState<'anonymous' | 'magic' | null>(null);

  const handleAnonymous = async () => {
    setIsLoading('anonymous');
    try {
      await onAnonymous();
    } finally {
      setIsLoading(null);
    }
  };

  const handleMagicLink = async () => {
    setIsLoading('magic');
    try {
      await onMagicLink();
    } finally {
      setIsLoading(null);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
          onClick={onClose}
        />
        
        {/* Dialog */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative max-w-md w-full mx-4 bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-xl rounded-2xl border border-white/10 p-6 shadow-2xl"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1 text-gray-400 hover:text-white transition-colors"
            disabled={isLoading !== null}
          >
            <X className="w-5 h-5" />
          </button>

          {/* Header */}
          <div className="mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center mb-4">
              <Mail className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl font-semibold text-white mb-2">
              Email Already Used
            </h2>
            <p className="text-gray-300">
              We found previous tests associated with{' '}
              <span className="font-medium text-white">{email}</span>.
              How would you like to continue?
            </p>
          </div>

          {/* Options */}
          <div className="space-y-3 mb-6">
            {/* Continue Anonymously */}
            <button
              onClick={handleAnonymous}
              disabled={isLoading !== null}
              className="w-full p-4 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <UserPlus className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center">
                    <h3 className="font-medium text-white">Continue Anonymously</h3>
                    {isLoading === 'anonymous' && (
                      <div className="ml-2 w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    )}
                  </div>
                  <p className="text-sm text-gray-400 mt-1">
                    Create a new test without accessing previous results
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors flex-shrink-0" />
              </div>
            </button>

            {/* Access My Account */}
            <button
              onClick={handleMagicLink}
              disabled={isLoading !== null}
              className="w-full p-4 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-xl hover:from-purple-600/30 hover:to-pink-600/30 focus:outline-none focus:ring-2 focus:ring-purple-500 transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                  <Mail className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center">
                    <h3 className="font-medium text-white">Access My Account</h3>
                    {isLoading === 'magic' && (
                      <div className="ml-2 w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    )}
                  </div>
                  <p className="text-sm text-gray-400 mt-1">
                    Get a magic link to see all your previous tests
                  </p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-white transition-colors flex-shrink-0" />
              </div>
            </button>
          </div>

          {/* Footer */}
          <p className="text-xs text-gray-500 text-center">
            Your privacy is our priority. Magic links expire in 15 minutes.
          </p>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}